/**
 * Shim for `react-native-vector-icons/*`.
 *
 * Renders icons using the Google "Material Icons" web font (loaded on demand).
 * React Native MaterialIcons names map to Material ligatures by swapping `-`
 * for `_`. A small alias table covers the Ionicons/FontAwesome names this app
 * uses so they resolve to an equivalent Material glyph.
 */
import React, { useCallback } from 'react';
import { toCss } from '../web/styleUtils';
import type { StyleProp } from '../web/styleUtils';

let fontInjected = false;
function ensureFont() {
  if (fontInjected || typeof document === 'undefined') return;
  fontInjected = true;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/icon?family=Material+Icons';
  document.head.appendChild(link);
}

// Names from other icon sets → nearest Material Icons ligature.
const ALIASES: Record<string, string> = {
  plus: 'add',
  'plus-circle-outline': 'add_circle_outline',
  pencil: 'edit',
  'chevron-up': 'keyboard_arrow_up',
  'chevron-down': 'keyboard_arrow_down',
  'chevron-back': 'chevron_left',
  'chevron-forward': 'chevron_right',
  'ios-close': 'close',
  'ios-checkmark': 'check',
  'md-close': 'close',
  'arrow-back': 'arrow_back',
  'arrow-forward': 'arrow_forward',
  'navigate-next': 'navigate_next',
  'navigate-before': 'navigate_before',
  'calendar-today': 'calendar_today',
  'ellipsis-vertical': 'more_vert',
  'ellipsis-horizontal': 'more_horiz',
  
};

function toLigature(name: string): string {
  if (!name) return '';
  if (ALIASES[name]) return ALIASES[name];
  return name.replace(/-/g, '_');
}

export interface IconProps {
  name: string;
  size?: number;
  color?: string;
  style?: StyleProp;
  onPress?: () => void;
  testID?: string;
  [key: string]: any;
}

const Icon = function Icon({ name, size = 24, color = '#000', style, onPress, testID }: IconProps) {
  ensureFont();
  const handleClick = useCallback(() => onPress?.(), [onPress]);
  return (
    <span
      className="material-icons"
      data-testid={testID}
      onClick={onPress ? handleClick : undefined}
      style={{
        fontSize: size,
        color,
        lineHeight: 1,
        userSelect: 'none',
        cursor: onPress ? 'pointer' : 'inherit',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...(toCss(style) as any),
      }}
    >
      {toLigature(name)}
    </span>
  );
};

// react-native-vector-icons exposes an `Icon.Button` helper in some code paths.
(Icon as any).Button = function IconButton({ name, size, color, children, onPress, style, backgroundColor }: any) {
  return (
    <button
      onClick={onPress}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        border: 'none',
        cursor: 'pointer',
        background: backgroundColor ?? 'transparent',
        ...(toCss(style) as any),
      }}
    >
      <Icon name={name} size={size} color={color} />
      {children}
    </button>
  );
};

export function createIconSet() {
  return Icon;
}
export function createIconSetFromFontello() {
  return Icon;
}
export function createIconSetFromIcoMoon() {
  return Icon;
}

export default Icon;
